import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { Loader2, MapPin, Search } from 'lucide-react';
import api from '../lib/api';

/**
 * ENA Connect Direct address autocomplete. Type an address, pick a street-level
 * suggestion. Fails soft: if ENA isn't configured / errors, it behaves as a
 * plain text input (staff can still type freely).
 *
 * Props:
 *   value      — current text value (controlled)
 *   onChange   — (text) => void, fires on every keystroke
 *   onSelect   — (suggestion) => void, fires when a suggestion is chosen
 *                suggestion = { label, address_line_1, city, county, postcode, uprn }
 *   placeholder, testId, className
 */
export const AddressAutocomplete = ({
  value = '', onChange, onSelect, placeholder = 'Start typing an address…',
  testId = 'address-autocomplete', className = '',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);
  const skipNextFetch = useRef(false);

  const fetchSuggestions = useCallback(async (term) => {
    if (!term || term.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/dno/address-autocomplete', { params: { q: term } });
      const list = data.suggestions || [];
      setSuggestions(list);
      setOpen(list.length > 0);
      setActiveIdx(-1);
    } catch {
      setSuggestions([]); setOpen(false);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, fetchSuggestions]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const choose = (s) => {
    skipNextFetch.current = true;
    onChange?.(s.address_line_1 || s.label);
    onSelect?.(s);
    setOpen(false);
    setSuggestions([]);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); choose(suggestions[activeIdx]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="pl-9"
          data-testid={testId}
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-auto" data-testid={`${testId}-list`}>
          {suggestions.map((s, i) => (
            <button
              key={`${s.uprn || s.label}-${i}`}
              type="button"
              onClick={() => choose(s)}
              className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 hover:bg-emerald-50 ${i === activeIdx ? 'bg-emerald-50' : ''}`}
              data-testid={`${testId}-option-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
              <span className="text-slate-700">{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
